<template>
  <div>
    <!-- TABLE ACTION ROW -->
    <div class="flex flex-wrap justify-between">
      <!-- rowData PER PAGE -->
      <div class="mb-4 md:mb-0 mr-4">
        <vs-dropdown
          v-if="pagination"
          vs-trigger-click
          class="cursor-pointer"
        >
          <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex justify-between font-medium">
            <span class="mr-2">{{
              innerTotal === 0 ? 0 : innerCurrentPage * innerPaginationPageSize - (innerPaginationPageSize - 1)
            }} - {{
              innerTotal - innerCurrentPage * innerPaginationPageSize > 0 ? innerCurrentPage * innerPaginationPageSize : innerTotal
            }} of {{ innerTotal }}</span>
            <VueFeather
              type="chevron-down"
              class="feather-icon feather-icon-css-target"
            />
          </div>
          <vs-dropdown-menu>
            <span @click="onPageSizeChange(20)">
              <vs-dropdown-item>
                <span>20</span>
              </vs-dropdown-item>
            </span>
            <span @click="onPageSizeChange(50)"> 
              <vs-dropdown-item>
                <span>50</span>
              </vs-dropdown-item>
            </span>
            <span @click="onPageSizeChange(100)">
              <vs-dropdown-item>
                <span>100</span>
              </vs-dropdown-item>
            </span>
            <span @click="onPageSizeChange(150)">
              <vs-dropdown-item>
                <span>150</span>
              </vs-dropdown-item>
            </span>
          </vs-dropdown-menu>
        </vs-dropdown>
      </div>
      <div class="flex flex-wrap items-center justify-between">
        <input-field
          v-if="searchable"
          v-model="innerSearch"
          name="search"
          class="data-table__search-bar"
          icon="icon icon-search"
          icon-pack="feather"
          :placeholder="searchPlaceholder"
        />
        <vs-button
          v-if="exportable"
          class="mb-4 md:mb-0 mr-3"
          @click="gridApi.exportDataAsCsv()"
        >
          CSVエクスポート
        </vs-button>
        <slot name="actions" />
      </div>
    </div>
    <ag-grid-vue
      v-bind="$attrs"
      ref="agGridTable"
      :grid-options="gridOptions"
      :column-defs="innerColumnDefs"
      :default-col-def="defaultColDef"
      :row-data="rowData"
      :animate-rows="true"
      :pagination="pagination"
      :pagination-page-size="innerPaginationPageSize"
      :suppress-pagination-panel="true"
      :enable-rtl="false"
      class="ag-theme-material w-100 my-4 ag-grid-table"
      row-selection="single"
      col-resize-default="shift"
      enable-cell-text-selection
      :overlay-no-rows-template="overlayNoRowsTemplate"
      @grid-ready="onGridReady"
      @pagination-changed="onPaginationChanged"
      @cell-value-changed="onCellValueChanged"
      :domLayout="domLayout"
    />
    <vs-pagination
      v-if="pagination"
      v-model="innerCurrentPage"
      :total="totalPage"
    />
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import 'ag-grid-community/styles/ag-grid.min.css' // agGridStyleOverride.scss が 上書きをするのでその前に持ってくる
import 'ag-grid-community/styles/ag-theme-material.css' // agGridStyleOverride.scss が 上書きをするのでその前に持ってくる
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'
import InputField from '@/components/fields/InputField'

// v-bind="$attrs"でpropsに定義されてないパラメータをすべて渡してる
// 有効なパラメータ一覧
// https://www.ag-grid.com/vue-data-grid/grid-properties/
// v-on="$listeners"でリスナーすべて渡してる
// リッスン可能なイベント一覧
// https://www.ag-grid.com/vue-data-grid/grid-events/

export default {
  name: 'DataTable',
  components: {
    InputField,
    AgGridVue
  },
  props: {
    search: {
      type: String,
      default: null
    },
    searchPlaceholder: {
      type: String,
      default: null
    },
    rowData: {
      type: Array,
      default () {
        return []
      }
    },
    columnDefs: {
      type: Array,
      default () {
        return []
      }
    },
    defaultColDef: {
      type: Object,
      default () {
        return {
          sortable: true,
          editable: false,
          resizable: false,
          suppressMenu: true
        }
      }
    },
    defaultSortModel: {
      type: Array,
      default () {
        return []
      }
    },
    exportable: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    paginationPageSize: {
      type: Number,
      default: 50
    },
    total: {
      type: Number,
      default: -1
    },
    gridOptions: {
      type: Object,
      default () {
        return {}
      }
    },
    columnsToFit: {
      type: Boolean,
      default: false
    },
    resetCurrentPageFlag: {
      type: Boolean,
      default: false
    },
    domLayout: {
      type: String,
      default: 'normal'
    }
  },
  emits: ['update:current-page', 'update:reset-current-page-flag', 'update:pagination-page-size', 'update:search', 'update:cell-value'],
  data () {
    return {
      gridApi: null,
      innerCurrentPage: 1,
      totalPage: 0,
      innerPaginationPageSize: 0,
      overlayNoRowsTemplate: null
    }
  },
  computed: {
    paginateInFront () {
      return this.total === -1
    },
    innerTotal () {
      return this.total > 0 ? this.total : this.rowData.length
    },
    innerSearch: {
      get () {
        return this.search
      },
      set (value) {
        this.$emit('update:search', value)
      }
    },
    innerColumnDefs () {
      return this.columnDefs.map(columnDef => {
        // valueFormatterがある場合は、valueFormatterを使用する
        if (columnDef.valueFormatter) {
          return columnDef
        }
        const { currency = null, percent = null, ...props } = columnDef
        // 通貨形式にする
        if (currency) {
          return {
            ...props,
            valueFormatter: this.currencyFormatter
          }
        }
        // ％表示にする
        if (percent) {
          return {
            ...props,
            valueFormatter: this.percentFormatter
          }
        }
        // 数値の場合はカンマをつける
        return {
          ...props,
          valueFormatter: this.defaultFormatter
        }
      })
    }
  },
  watch: {
    innerCurrentPage (value) {
      if (this.paginateInFront && this.gridApi) { // ページネーションを全てフロントで処理する
        this.gridApi.paginationGoToPage(value - 1)
      } else {
        this.$emit('update:current-page', value)
      }
    },
    innerPaginationPageSize (value) {
      if (this.paginateInFront && this.gridApi) { // ページネーションを全てフロントで処理する
        this.gridApi.setGridOption('paginationPageSize', value)
      } else {
        this.$emit('update:pagination-page-size', value)
      }
    },
    resetCurrentPageFlag (value) {  // 親コンポーネントからページリセットする
      if ( value ) {
        this.innerCurrentPage = 1
        this.$emit('update:reset-current-page-flag')
      }
    }
  },
  mounted () {
    // 初期化
    this.innerPaginationPageSize = this.paginationPageSize
    this.innerCurrentPage = this.currentPage
  },
  created () {
    this.overlayNoRowsTemplate = 'No Data'
  },
  methods: {
    onPageSizeChange (value) {
      this.innerPaginationPageSize = value
      this.innerCurrentPage = 1
    },
    onGridReady (params) {
      this.gridApi = params.api
      if (this.columnsToFit) {
        // データテーブルの横幅を画面にフィットさせる
        this.gridApi.sizeColumnsToFit()
        // MEMO: +1pxで横幅の小数点切り上げ分のはみ出しをカバーする
        //       +1pxがないとFitさせた状態でも横のスクロールバーが表示されてしまう
        const colsViewport = this.$refs.agGridTable.$el.querySelector('.ag-center-cols-viewport')
        colsViewport.style.width = 'calc(100% + 1px)'
        // 各行の下線を右端まで表示するため100%に設定
        const colsContainer = this.$refs.agGridTable.$el.querySelector('.ag-center-cols-container')
        colsContainer.style.width = '100%'
      }
      // デフォルトソート
      if (this.defaultSortModel.length > 0) {
        this.gridApi.applyColumnState({ state: this.defaultSortModel });
      }
    },
    onPaginationChanged () {
      if (this.paginateInFront) { // ページネーションを全てフロントで処理する
        this.totalPage = this.gridApi ? this.gridApi.paginationGetTotalPages() : 0
      } else {
        this.totalPage = Math.ceil(this.total / this.innerPaginationPageSize)
      }
    },
    onCellValueChanged (params) {
      this.$emit('update:cell-value', params)
    },
    currencyFormatter (params) {
      let v = ''
      if (typeof params.value === 'number') {
        v = `\xA5${params.value.toLocaleString()}`
      }
      return v
    },
    percentFormatter (params) {
      if (typeof params.value !== 'number') {
        return ''
      }
      const base = 10 ** 2
      const value = Math.round((params.value * 100) * base) / base
      return `${value.toLocaleString()}%`
    },
    defaultFormatter (params) {
      if (typeof params.value !== 'number') {
        return params.value
      }
      return params.value.toLocaleString()
    }
  }
}
</script>

<style>
.data-table__search-bar > div.vs-input {
  min-width: 250px;
}
</style>

<style scoped>
.feather-icon-css-target :deep(.vue-feather__content) {
  width: 1rem !important;
  height: 1rem !important;
}
</style>
